import React, { useState, useEffect } from 'react';
import { 
  PackageCheck, 
  Plus, 
  Search,
  Trash2,
  Eye,
  ClipboardCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '../context/SettingsContext';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GoodsReceipt = () => {
  const { currencySymbol } = useSettings();
  const [receipts, setReceipts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewingReceipt, setViewingReceipt] = useState(null);
  const [selectedPO, setSelectedPO] = useState(null);
  const [formData, setFormData] = useState({ items: [] });

  useEffect(() => {
    fetchReceipts();
    fetchPendingOrders();
  }, []);

  const fetchReceipts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/procurement/grn`);
      setReceipts(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingOrders = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/procurement/purchase-orders`);
      setOrders(res.data.filter(o => ['sent', 'partial'].includes(o.status)));
    } catch (err) {
      console.error(err);
    }
  };

  const handleSelectPO = (poId) => {
    const po = orders.find(o => o.id === poId);
    setSelectedPO(po);
    if (po) {
      setFormData({
        po_id: po.id,
        items: po.items.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          quantity_ordered: item.quantity,
          quantity_received: item.quantity - (item.received_quantity || 0),
          unit_price: item.unit_price
        }))
      });
    }
  };

  const handleSave = async () => {
    try {
      await axios.post(`${API_URL}/api/procurement/grn`, formData);
      toast.success('Goods received and stock updated');
      setShowDialog(false);
      setFormData({ items: [] });
      setSelectedPO(null);
      fetchReceipts();
      fetchPendingOrders();
    } catch (err) {
      toast.error('Failed to create GRN');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this receipt?')) {
      try {
        await axios.delete(`${API_URL}/api/procurement/grn/${id}`);
        toast.success('GRN deleted');
        fetchReceipts();
      } catch (err) {
        toast.error('Failed to delete');
      }
    }
  };

  const openNew = () => {
    setSelectedPO(null);
    setFormData({ items: [] });
    setShowDialog(true);
  };

  const openView = (receipt) => {
    setViewingReceipt(receipt);
    setShowViewDialog(true);
  };

  const updateItemQty = (index, qty) => {
    const newItems = [...formData.items];
    newItems[index].quantity_received = qty;
    setFormData({ ...formData, items: newItems });
  };

  const stats = {
    total: receipts.length,
    pendingPOs: orders.length,
    thisMonth: receipts.filter(r => {
      const d = new Date(r.date);
      const now = new Date();
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Goods Receipt</h1>
          <p className="text-muted-foreground">Receive materials against purchase orders.</p>
        </div>
        <Button onClick={openNew} className="gap-2" disabled={orders.length === 0}>
          <Plus className="h-4 w-4" /> Receive Goods
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{stats.total}</div><div className="text-sm text-muted-foreground">Total GRNs</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-blue-600">{stats.pendingPOs}</div><div className="text-sm text-muted-foreground">Pending POs</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-emerald-600">{stats.thisMonth}</div><div className="text-sm text-muted-foreground">This Month</div></CardContent></Card>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>GRN Number</TableHead>
              <TableHead>PO Number</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Items</TableHead>
              <TableHead>Received By</TableHead>
              <TableHead className="w-[100px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {receipts.map((receipt) => (
              <TableRow key={receipt.id}>
                <TableCell className="font-mono font-medium">{receipt.grn_number}</TableCell>
                <TableCell className="font-mono text-muted-foreground">{receipt.po_number}</TableCell>
                <TableCell>{receipt.supplier_name}</TableCell>
                <TableCell className="text-muted-foreground">{format(new Date(receipt.date), 'MMM d, yyyy')}</TableCell>
                <TableCell>{receipt.items?.length || 0} items</TableCell>
                <TableCell>{receipt.received_by || '-'}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openView(receipt)}>
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(receipt.id)}>
                      <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {receipts.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="h-32 text-center">
                  <PackageCheck className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
                  <p className="text-muted-foreground">No goods receipts found</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create GRN Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Receive Goods</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Select Purchase Order *</Label>
                <Select value={formData.po_id || ''} onValueChange={handleSelectPO}>
                  <SelectTrigger><SelectValue placeholder="Select PO" /></SelectTrigger>
                  <SelectContent>
                    {orders.map(o => (
                      <SelectItem key={o.id} value={o.id}>
                        {o.po_number} - {o.supplier_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Received By</Label>
                <Input value={formData.received_by || ''} onChange={e => setFormData({...formData, received_by: e.target.value})} placeholder="Name" />
              </div>
            </div>

            {selectedPO && (
              <>
                <div className="bg-muted/50 rounded-lg p-3 text-sm">
                  <div className="grid grid-cols-2 gap-2">
                    <div><span className="text-muted-foreground">Supplier:</span> {selectedPO.supplier_name}</div>
                    <div><span className="text-muted-foreground">PO Date:</span> {format(new Date(selectedPO.date), 'MMM d, yyyy')}</div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Items to Receive</Label>
                  <div className="border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Product</TableHead>
                          <TableHead className="text-right">Ordered</TableHead>
                          <TableHead className="text-right">Previously Received</TableHead>
                          <TableHead className="text-right">Receiving Now</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {formData.items.map((item, idx) => {
                          const poItem = selectedPO.items.find(i => i.product_id === item.product_id);
                          const prevReceived = poItem?.received_quantity || 0;
                          return (
                            <TableRow key={idx}>
                              <TableCell className="font-medium">{item.product_name}</TableCell>
                              <TableCell className="text-right">{item.quantity_ordered}</TableCell>
                              <TableCell className="text-right text-muted-foreground">{prevReceived}</TableCell>
                              <TableCell className="text-right">
                                <Input 
                                  type="number" 
                                  min="0" 
                                  max={item.quantity_ordered - prevReceived}
                                  className="w-24 ml-auto" 
                                  value={item.quantity_received} 
                                  onChange={e => updateItemQty(idx, parseFloat(e.target.value) || 0)} 
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={formData.notes || ''} onChange={e => setFormData({...formData, notes: e.target.value})} rows={2} placeholder="Any notes about the delivery..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={!selectedPO}>
              <ClipboardCheck className="h-4 w-4 mr-2" /> Confirm Receipt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View GRN Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>GRN - {viewingReceipt?.grn_number}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-muted-foreground">PO Number:</span> <span className="font-mono">{viewingReceipt?.po_number}</span></div>
              <div><span className="text-muted-foreground">Supplier:</span> <span className="font-medium">{viewingReceipt?.supplier_name}</span></div>
              <div><span className="text-muted-foreground">Date:</span> {viewingReceipt?.date && format(new Date(viewingReceipt.date), 'MMM d, yyyy')}</div>
              <div><span className="text-muted-foreground">Received By:</span> {viewingReceipt?.received_by || '-'}</div>
            </div>

            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead className="text-right">Ordered</TableHead>
                    <TableHead className="text-right">Received</TableHead>
                    <TableHead className="text-right">Unit Price</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {viewingReceipt?.items?.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{item.product_name}</TableCell>
                      <TableCell className="text-right">{item.quantity_ordered}</TableCell>
                      <TableCell className="text-right font-medium text-emerald-600">{item.quantity_received}</TableCell>
                      <TableCell className="text-right font-mono">{currencySymbol} {item.unit_price?.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {viewingReceipt?.notes && (
              <div className="bg-muted/50 rounded-lg p-3">
                <div className="text-sm text-muted-foreground">Notes:</div>
                <div className="text-sm">{viewingReceipt.notes}</div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GoodsReceipt;
